<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import {
  Add01Icon,
  AiChat02Icon,
  ArrowDown01Icon,
  ArrowUp01Icon,
  CallIncoming01Icon,
  Cancel01Icon,
  InboxDownloadIcon,
  PanelLeftOpenIcon,
  Search01Icon,
  UserRemove01Icon,
  UserSquareIcon,
} from '@hugeicons/core-free-icons';
import { useMapGetter } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import HugeIcon from 'dashboard/components-next/icon/HugeIcon.vue';
import wootConstants from 'dashboard/constants/globals';
import ConversationApi from 'dashboard/api/inbox/conversation';

const props = defineProps({
  activeAssigneeTab: {
    type: String,
    default: wootConstants.ASSIGNEE_TYPE.ME,
  },
  customConversationFilter: {
    type: Object,
    default: null,
  },
  isVisible: {
    type: Boolean,
    default: true,
  },
  conversationMetaParams: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['assigneeChange', 'collapse', 'filterChange']);

function childRowClasses(active) {
  return [
    'mb-1 flex h-8 w-full items-center gap-2 rounded-md border px-2 text-sm transition-colors',
    active
      ? 'border-transparent bg-n-alpha-2 font-semibold text-n-slate-12 hover:text-n-slate-12'
      : 'border-transparent text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12',
  ];
}

function assigneeRowClasses(active) {
  return [
    'mb-1 flex h-8 w-full items-center gap-2 rounded-md border px-2 text-sm transition-colors',
    active
      ? 'border-transparent bg-n-alpha-2 font-semibold text-n-slate-12 hover:text-n-slate-12'
      : 'border-transparent text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12',
  ];
}

function plainCountClasses(active) {
  return active
    ? 'shrink-0 text-xs font-semibold tabular-nums text-n-slate-12'
    : 'shrink-0 text-xs tabular-nums text-n-slate-10';
}

function isAiAgentRowActive(agent) {
  const f = props.customConversationFilter;
  return f?.type === 'ai_agent' && String(f?.id) === String(agent.id);
}

function isLifecycleRowActive(stage) {
  const f = props.customConversationFilter;
  return f?.type === 'lifecycle' && String(f?.id) === String(stage.id);
}

function isTeamRowActive(team) {
  const f = props.customConversationFilter;
  return f?.type === 'team' && String(f?.id) === String(team.id);
}

const store = useStore();
const { t } = useI18n();
const search = ref('');
const showSearch = ref(false);
const showAiAgents = ref(true);
const showLifecycle = ref(true);
const showTeamInbox = ref(true);
const showCustomInbox = ref(true);
const conversationStats = useMapGetter('conversationStats/getStats');
const aiAgents = useMapGetter('aiAgents/getAgents');
const lifecycleStages = useMapGetter('lifecycleStages/getLifecycleStages');
const teams = useMapGetter('teams/getTeams');
const folders = useMapGetter('customViews/getConversationCustomViews');

const lifecycleStageCounts = ref({});
const teamCounts = ref({});
const assigneeAgentBotCounts = ref({});

const assigneeItems = computed(() => [
  {
    key: wootConstants.ASSIGNEE_TYPE.ALL,
    label: t('CHAT_LIST.ASSIGNEE_TYPE_TABS.all'),
    icon: InboxDownloadIcon,
    count: conversationStats.value.allCount ?? 0,
    countVariant: 'plain',
  },
  {
    key: wootConstants.ASSIGNEE_TYPE.ME,
    label: t('CHAT_LIST.ASSIGNEE_TYPE_TABS.me'),
    icon: UserSquareIcon,
    count: conversationStats.value.mineCount ?? 0,
    countVariant: 'plain',
  },
  {
    key: wootConstants.ASSIGNEE_TYPE.UNASSIGNED,
    label: t('CHAT_LIST.ASSIGNEE_TYPE_TABS.unassigned'),
    icon: UserRemove01Icon,
    count: conversationStats.value.unAssignedCount ?? 0,
    countVariant: 'badge',
  },
]);

const filteredAiAgents = computed(() => {
  const publishedAgents = aiAgents.value.filter(
    agent => agent.status === 'published'
  );
  if (!search.value) return publishedAgents;
  return publishedAgents.filter(agent =>
    agent.name?.toLowerCase().includes(search.value.toLowerCase())
  );
});

const filteredLifecycleStages = computed(() => {
  if (!search.value) return lifecycleStages.value;
  return lifecycleStages.value.filter(stage =>
    stage.name?.toLowerCase().includes(search.value.toLowerCase())
  );
});

const filteredTeams = computed(() => {
  if (!search.value) return teams.value;
  return teams.value.filter(team =>
    team.name?.toLowerCase().includes(search.value.toLowerCase())
  );
});

function lifecycleStageCount(stage) {
  return lifecycleStageCounts.value[String(stage.id)] ?? 0;
}

function teamConversationCount(team) {
  return teamCounts.value[String(team.id)] ?? 0;
}

function agentConversationCount(agent) {
  const botId = agent.agent_bot_id;
  if (!botId) return 0;
  return assigneeAgentBotCounts.value[String(botId)] ?? 0;
}

async function fetchSidebarBreakdown() {
  if (!props.isVisible || !props.conversationMetaParams) {
    return;
  }
  const p = props.conversationMetaParams;
  try {
    const { data } = await ConversationApi.meta({
      inboxId: p.inboxId,
      status: p.status,
      teamId: p.teamId,
      conversationType: p.conversationType,
      labels: p.labels,
      includeSidebarBreakdown: true,
    });
    const meta = data.meta || {};
    lifecycleStageCounts.value = meta.lifecycle_stage_counts || {};
    teamCounts.value = meta.team_counts || {};
    assigneeAgentBotCounts.value = meta.assignee_agent_bot_counts || {};
  } catch {
    // Optional breakdown; ignore failures
  }
}

const debouncedFetchSidebarBreakdown = debounce(
  fetchSidebarBreakdown,
  400,
  false,
  1200
);

watch(
  () => [props.conversationMetaParams, props.isVisible],
  () => {
    debouncedFetchSidebarBreakdown();
  },
  { deep: true }
);

useEmitter('fetch_conversation_stats', () => {
  debouncedFetchSidebarBreakdown();
});

onMounted(() => {
  store.dispatch('aiAgents/get');
  store.dispatch('lifecycleStages/get');
  store.dispatch('teams/get');
  store.dispatch('customViews/get', 'conversation');
  debouncedFetchSidebarBreakdown();
});

function closeSearch() {
  search.value = '';
  showSearch.value = false;
}

function selectAssignee(key) {
  emit('filterChange', null);
  emit('assigneeChange', key);
}

function selectAiAgent(agent) {
  emit('assigneeChange', wootConstants.ASSIGNEE_TYPE.ALL);
  emit('filterChange', {
    type: 'ai_agent',
    id: agent.id,
    name: agent.name,
  });
}

function selectTeam(team) {
  emit('assigneeChange', wootConstants.ASSIGNEE_TYPE.ALL);
  emit('filterChange', {
    type: 'team',
    id: team.id,
    name: team.name,
  });
}

function selectLifecycleStage(stage) {
  emit('assigneeChange', wootConstants.ASSIGNEE_TYPE.ALL);
  emit('filterChange', {
    type: 'lifecycle',
    id: stage.id,
    name: stage.name,
  });
}
</script>

<template>
  <aside
    class="hidden h-full w-[216px] shrink-0 border-r border-n-weak bg-n-surface-1 md:flex md:flex-col"
    :class="{ '!hidden': !isVisible }"
  >
    <div v-if="showSearch" class="flex h-12 items-center gap-1 px-2">
      <div
        class="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-md border border-n-weak bg-n-alpha-1 px-3 transition-colors focus-within:border-n-strong"
      >
        <HugeIcon
          :icon="Search01Icon"
          :size="20"
          class="shrink-0 text-n-slate-12"
        />
        <input
          v-model="search"
          type="text"
          :placeholder="$t('CHAT_LIST.OMNI_SIDEBAR.SEARCH')"
          class="reset-base h-5 min-w-0 flex-1 appearance-none self-center border-0 bg-transparent p-0 text-sm leading-5 text-n-slate-12 shadow-none outline-none ring-0 placeholder:text-n-slate-10 focus:border-0 focus:shadow-none focus:outline-none focus:ring-0 focus-visible:outline-none"
          autofocus
        />
      </div>
      <button
        class="flex size-8 items-center justify-center rounded-md text-n-slate-12 hover:bg-n-alpha-2"
        type="button"
        @click="closeSearch"
      >
        <HugeIcon :icon="Cancel01Icon" :size="20" class="shrink-0" />
      </button>
    </div>
    <div v-else class="flex h-12 items-center gap-2 px-3">
      <h2
        class="min-w-0 flex-1 truncate text-base font-semibold text-n-slate-12"
      >
        {{ $t('CHAT_LIST.OMNI_SIDEBAR.INBOX') }}
      </h2>
      <button
        class="flex size-7 items-center justify-center rounded-md border border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
        type="button"
        @click="showSearch = true"
      >
        <HugeIcon :icon="Search01Icon" :size="20" class="shrink-0" />
      </button>
      <button
        class="flex size-7 items-center justify-center rounded-md border border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
        type="button"
        @click="emit('collapse')"
      >
        <HugeIcon :icon="PanelLeftOpenIcon" :size="20" class="shrink-0" />
      </button>
    </div>

    <nav class="flex-1 overflow-y-auto px-2 pb-4">
      <button
        v-for="item in assigneeItems"
        :key="item.key"
        type="button"
        :class="assigneeRowClasses(activeAssigneeTab === item.key)"
        @click="selectAssignee(item.key)"
      >
        <HugeIcon
          :icon="item.icon"
          :size="18"
          class="shrink-0"
          :class="
            activeAssigneeTab === item.key
              ? 'text-n-slate-12'
              : 'text-n-slate-11'
          "
        />
        <span class="min-w-0 flex-1 truncate text-start">{{ item.label }}</span>
        <span
          v-if="item.countVariant === 'badge'"
          class="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-n-brand px-1.5 text-[11px] font-semibold tabular-nums text-white"
        >
          {{ item.count }}
        </span>
        <span v-else :class="plainCountClasses(activeAssigneeTab === item.key)">
          {{ item.count }}
        </span>
      </button>

      <button
        type="button"
        disabled
        class="mb-4 flex h-8 w-full items-center gap-2 rounded-md px-2 text-sm text-n-slate-10"
      >
        <HugeIcon :icon="CallIncoming01Icon" :size="18" class="shrink-0" />
        <span class="min-w-0 flex-1 truncate text-left">
          {{ $t('CHAT_LIST.OMNI_SIDEBAR.INCOMING_CALLS') }}
        </span>
        <span class="rounded bg-n-slate-3 px-1.5 py-0.5 text-[10px] uppercase">
          {{ $t('CHAT_LIST.OMNI_SIDEBAR.SOON') }}
        </span>
      </button>

      <div>
        <div class="mb-2">
          <button
            type="button"
            class="mb-1 flex h-8 w-full items-center justify-between px-2 text-xs font-medium text-n-slate-11"
            @click="showAiAgents = !showAiAgents"
          >
            <span class="inline-flex items-center gap-2">
              <HugeIcon :icon="AiChat02Icon" :size="18" class="shrink-0" />
              <span>{{ $t('CHAT_LIST.OMNI_SIDEBAR.AI_AGENTS') }}</span>
            </span>
            <HugeIcon
              :icon="showAiAgents ? ArrowUp01Icon : ArrowDown01Icon"
              :size="18"
              class="shrink-0"
            />
          </button>
          <div v-if="showAiAgents">
            <button
              v-for="agent in filteredAiAgents"
              :key="agent.id"
              type="button"
              :class="childRowClasses(isAiAgentRowActive(agent))"
              @click="selectAiAgent(agent)"
            >
              <span
                class="inline-flex size-[18px] shrink-0 items-center justify-center text-[18px] leading-none"
              >
                {{ agent.icon || '🤑' }}
              </span>
              <span class="min-w-0 flex-1 truncate text-start">{{
                agent.name
              }}</span>
              <span
                class="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-n-brand px-1.5 text-[11px] font-semibold tabular-nums text-white"
              >
                {{ agentConversationCount(agent) }}
              </span>
            </button>
          </div>
        </div>

        <div class="mb-2">
          <button
            type="button"
            class="mb-1 flex h-8 w-full items-center justify-between px-2 text-xs font-medium text-n-slate-11"
            @click="showLifecycle = !showLifecycle"
          >
            <span>{{ $t('CHAT_LIST.OMNI_SIDEBAR.LIFECYCLE') }}</span>
            <HugeIcon
              :icon="showLifecycle ? ArrowUp01Icon : ArrowDown01Icon"
              :size="18"
              class="shrink-0"
            />
          </button>
          <div v-if="showLifecycle">
            <button
              v-for="stage in filteredLifecycleStages"
              :key="stage.id"
              type="button"
              :class="childRowClasses(isLifecycleRowActive(stage))"
              @click="selectLifecycleStage(stage)"
            >
              <span
                class="inline-flex size-[18px] shrink-0 items-center justify-center text-[18px] leading-none"
              >
                {{ stage.icon }}
              </span>
              <span class="min-w-0 flex-1 truncate text-start">{{
                stage.name
              }}</span>
              <span
                class="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-n-brand px-1.5 text-[11px] font-semibold tabular-nums text-white"
              >
                {{ lifecycleStageCount(stage) }}
              </span>
            </button>
          </div>
        </div>

        <div class="mb-2">
          <button
            type="button"
            class="mb-1 flex h-8 w-full items-center justify-between px-2 text-xs font-medium text-n-slate-11"
            @click="showTeamInbox = !showTeamInbox"
          >
            <span>{{ $t('CHAT_LIST.OMNI_SIDEBAR.TEAM_INBOX') }}</span>
            <span class="flex items-center gap-2">
              <HugeIcon :icon="Add01Icon" :size="18" class="shrink-0" />
              <HugeIcon
                :icon="showTeamInbox ? ArrowUp01Icon : ArrowDown01Icon"
                :size="18"
                class="shrink-0"
              />
            </span>
          </button>
          <div v-if="showTeamInbox">
            <button
              v-for="team in filteredTeams"
              :key="team.id"
              type="button"
              :class="childRowClasses(isTeamRowActive(team))"
              @click="selectTeam(team)"
            >
              <span class="min-w-0 flex-1 truncate text-start">{{
                team.name
              }}</span>
              <span class="shrink-0 text-xs tabular-nums text-n-slate-10">
                {{ teamConversationCount(team) }}
              </span>
            </button>
          </div>
        </div>

        <div>
          <button
            type="button"
            class="mb-1 flex h-8 w-full items-center justify-between px-2 text-xs font-medium text-n-slate-11"
            @click="showCustomInbox = !showCustomInbox"
          >
            <span>{{ $t('CHAT_LIST.OMNI_SIDEBAR.CUSTOM_INBOX') }}</span>
            <span class="flex items-center gap-2">
              <HugeIcon :icon="Add01Icon" :size="18" class="shrink-0" />
              <HugeIcon
                :icon="showCustomInbox ? ArrowUp01Icon : ArrowDown01Icon"
                :size="18"
                class="shrink-0"
              />
            </span>
          </button>
          <p
            v-if="showCustomInbox && !folders.length"
            class="px-2 py-1 text-xs font-medium text-n-slate-10"
          >
            {{ $t('CHAT_LIST.OMNI_SIDEBAR.NO_INBOXES') }}
          </p>
        </div>
      </div>
    </nav>
  </aside>
</template>
